import * as React from "react";
import {VeBoxCard} from "../../components/card/VeBoxCard";
import {useUserDetails} from "../contexts/UserDetailsContext";
import {Loader} from "../../components/Utils/loader/Loader";
import {Display} from "../../components/Display";
import {Button, InputAddon, TextInput, Tooltip} from "@vebox/etui-core";
import {VeboxTreeView} from "../../components/tree_view/VeboxTreeView";
import {commercialHierarchyGUID} from "../../processes/user_creation/stages/Visibility";
import {AppTranslations} from "../../components/Utils/translations/AppTranslations";
import {useCommonData} from "../contexts/CommonDataProvider";
import {HierarchyError, useUserHierarchyVisibility} from "../contexts/UserVisibilityHierarchiesContext";
import {
    addCeasedCompanyInHierarchy,
    changeSelectedCommercialHierarchies,
    changeSelectedHierarchy,
    checkIfIsSubmittable,
    CommercialHierarchyTreeToList,
    EditableHierarchy,
    expandCommercialHierarchy,
    getAllSelectedNodes,
    haveSameVisibility,
    HierarchiesSection,
    hierarchyIncludesWord,
    hierarchyToFilteredVeboxCheckboxTree,
    hierarchyToFilteredVeboxTreeItem,
    HierarchyTreeToList,
    IconInfoLegend,
    VisibilityIcon
} from "../HierarchyVisibilityUtils";
import {VISIBILITY_TREE_ICONS} from "../UserManagement";
import {SearchIcon} from "../components/SearchIcon";
import {RestDataStatus} from "../../hooks/useRestData";
import {last} from "../../processes/user_creation/UserCreation";
import {BUTTON_STYLE} from "../../components/common/etui_components_styles";
import {VeboxModels} from "../../types/veboxModels";
import {DATA_STATE} from "../contexts/UsersDashboardContext";
import {ErrorType} from "../UserDetails";

declare var AppTranslations: AppTranslations;

type ChangeUserHierarchyVisibilityProps = {
    setError: (errorType: ErrorType) => void
    hideEditVisibilitySection: () => void
}

export const ChangeUserHierarchyVisibility: React.FC<ChangeUserHierarchyVisibilityProps> = ({
                                                                                                setError,
                                                                                                hideEditVisibilitySection
                                                                                            }) => {

    const [currentUser] = useUserDetails();
    const [currentUserHierarchies, currentUserHierarchiesState, setNewUserVisibilityHierarchies] = useUserHierarchyVisibility();
    const {hierarchiesTree, hierarchiesTreeStatus} = useCommonData();

    const [state, setState] = React.useState<DATA_STATE>(DATA_STATE.isLoading)
    const [hasFullVisibility, setHasFullVisibility] = React.useState<boolean>();
    const [isInCeasedCompany, setIsInCeasedCompany] = React.useState<boolean>();
    const [hierarchiesSelected, setHierarchiesSelected] = React.useState<HierarchiesSection>();
    const [userHierarchiesSelected, setUserHierarchiesSelected] = React.useState<HierarchiesSection>();
    const [searchWord, setSearchWord] = React.useState("");
    const [commercialHierarchyExpanded, setCommercialHierarchyExpanded] = React.useState(false);
    const [isVisibilityChanged, setIsVisibilityChanged] = React.useState(false);
    const [isSubmittable, setIsSubmittable] = React.useState(false);
    const [completeHierarchiesTree, setCompleteHierarchiesTree] = React.useState<VeboxModels.HierarchiesTree[]>();
    const [commercialHierarchy, setCommercialHierarchy] = React.useState<VeboxModels.HierarchiesTree>();

    const [showSearchHierarchyBar, setShowSearchHierarchyBar] = React.useState<boolean>(false);

    const searchHierarchyBarRef = React.useRef(null)

    const showHideSearchHierarchyBar = () => {
        setShowSearchHierarchyBar(prevState => {
            if (prevState) {
                clearSearchWord()
            }
            return !prevState
        });
    }
    const submitNewVisibility = () => {
        setState(DATA_STATE.isLoading);
        setNewUserVisibilityHierarchies(hierarchiesSelected);
    }

    const setErrorState = (errorType: ErrorType) => {
        setError(errorType)
        setState(DATA_STATE.error);
    }

    const setDefaultVisibility = () => {
        let newSelectedHierarchy: HierarchiesSection = {
            selectedHierarchies: "all_companies",
            selectedCommercialHierarchies: []
        };
        let newHierarchiesTree: VeboxModels.HierarchiesTree[] = hierarchiesTree;
        let tmpCommercialHierarchy: VeboxModels.HierarchiesTree;

        if (Object.keys(currentUserHierarchies).length > 0) {
            if (currentUser.company.name.toLowerCase().includes("cessato")) {
                setIsInCeasedCompany(true);
                newHierarchiesTree = addCeasedCompanyInHierarchy(currentUserHierarchies, currentUser?.company,
                    hierarchiesTree)
            }
            tmpCommercialHierarchy = newHierarchiesTree.find(h => h.hierarchyGuid === commercialHierarchyGUID);
            setCommercialHierarchy(tmpCommercialHierarchy);

            if (currentUserHierarchies.hierarchyTypeGuid === commercialHierarchyGUID) {
                if (tmpCommercialHierarchy) {
                    newSelectedHierarchy = {
                        selectedHierarchies: [commercialHierarchyGUID],
                        selectedCommercialHierarchies: CommercialHierarchyTreeToList(
                            currentUserHierarchies.hierarchyNodesGuids,
                            tmpCommercialHierarchy)
                    };
                }
            } else if (!currentUserHierarchies.empty) {
                newSelectedHierarchy = {
                    selectedHierarchies: HierarchyTreeToList(currentUserHierarchies?.hierarchyNodesGuids[0],
                        newHierarchiesTree,
                        currentUserHierarchies?.hierarchyTypeGuid),
                    selectedCommercialHierarchies: []
                };
            }
        }
        setCompleteHierarchiesTree(newHierarchiesTree);
        setHasFullVisibility(newSelectedHierarchy.selectedHierarchies === "all_companies");
        setHierarchiesSelected(newSelectedHierarchy);
        setUserHierarchiesSelected(newSelectedHierarchy);
        setCommercialHierarchyExpanded(
            expandCommercialHierarchy(tmpCommercialHierarchy, newSelectedHierarchy, searchWord))
        setState(DATA_STATE.loaded);
    }

    const changeHierarchy = (v) => {
        setHierarchiesSelected(changeSelectedHierarchy(v, hierarchiesSelected));
    };

    const changeCommercialHierarchies = (v) => {
        setHierarchiesSelected(changeSelectedCommercialHierarchies(v, hierarchiesSelected));
    };

    const selectedCommercialNodes = getAllSelectedNodes(hierarchiesSelected?.selectedCommercialHierarchies ?? [])

    const resetValues = () => {
        clearSearchWord();
        setDefaultVisibility();
    };


    const clearSearchWord = () => setSearchWord("");

    const changeSearchWord = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchWord(e.target.value)
        expandCommercialHierarchy(commercialHierarchy, hierarchiesSelected, searchWord)
    };

    const renderBack: React.ReactNode = (
        <div className={"icons-container"}>
            <EditableHierarchy changeEditingStatus={hideEditVisibilitySection} isEditingMode={true}/>
            <SearchIcon
                showHideSearchBar={showHideSearchHierarchyBar}
                openSearchBarMessage={AppTranslations.filters.filterBy}
            />
            <IconInfoLegend showWarningIcon={isInCeasedCompany}/>
        </div>
    )


    React.useEffect(() => {
        if (searchHierarchyBarRef && showSearchHierarchyBar) {
            searchHierarchyBarRef.current.focus();
        }
    }, [showSearchHierarchyBar])

    React.useEffect(() => {
        switch (currentUserHierarchiesState) {
            case DATA_STATE.loaded: {
                if (hierarchiesTreeStatus === RestDataStatus.LOADED) {
                    setDefaultVisibility();
                }
                break;
            }
            case DATA_STATE.error: {
                setErrorState(ErrorType.CANNOT_RETRIEVE_USER_VISIBILITY_ERROR);
                break;
            }
            case HierarchyError.HIERARCHY_NOT_FOUND:
                // @ts-ignore
                setHierarchiesSelected({});
                setState(DATA_STATE.loaded);
                break;
            case HierarchyError.INVALID_ROLES_FOR_HIERARCHY:
                setErrorState(ErrorType.INVALID_ROLES_FOR_HIERARCHY);
                break;
        }
    }, [currentUserHierarchiesState, currentUserHierarchies, hierarchiesTreeStatus])


    React.useEffect(() => {
        if (hierarchiesSelected) {
            setHasFullVisibility(hierarchiesSelected?.selectedHierarchies === "all_companies");
            setIsSubmittable(checkIfIsSubmittable(hierarchiesSelected))
            if (userHierarchiesSelected) {
                setIsVisibilityChanged(!haveSameVisibility(hierarchiesSelected, userHierarchiesSelected))
            }
        }
    }, [hierarchiesSelected, userHierarchiesSelected]);

    return <>
        <VeBoxCard className={"Section"}>
            <VeBoxCard.Header
                title={AppTranslations.userCreationProcess.visibilityStageTitle}
                singleLineTitle={true}
                back={state === DATA_STATE.loaded && currentUserHierarchiesState === DATA_STATE.loaded ? renderBack : undefined}
            />
            <VeBoxCard.Body className={"detail-card"}>
                <Display isVisible={state === DATA_STATE.isLoading || currentUserHierarchiesState === DATA_STATE.isLoading}>
                    <Loader/>
                </Display>
                <Display isVisible={state === DATA_STATE.loaded && currentUserHierarchiesState === DATA_STATE.loaded}>
                    <TextInput
                        elRef={searchHierarchyBarRef}
                        label={AppTranslations.filters.filterBy}
                        styles={{
                            input: {boxSizing: "border-box"},
                            root: {display: (!showSearchHierarchyBar ? "none" : "")}
                        }}
                        className={"margin-bottom-10"}
                        value={searchWord}
                        onChange={changeSearchWord}
                        endInputAddon={
                            <InputAddon position={"end"}>
                                {Boolean(searchWord) && <span
                                    className="material-icons-outlined clickable"
                                    onClick={clearSearchWord}
                                >clear</span>}
                            </InputAddon>
                        }
                    />
                    <div
                        className={"users-management-list-container"}
                    >
                        <VeboxTreeView.RadioGroup
                            className={"hierarchy-tree"}
                            value={hierarchiesSelected?.selectedHierarchies ?? []}
                            onChange={changeHierarchy}
                        >
                            {(AppTranslations.userCreationProcess.allCompany).toLowerCase()
                                .includes(searchWord.toLowerCase())
                                ? <VeboxTreeView.Radio
                                    label={
                                        <>
                                            <Display isVisible={hasFullVisibility}>
                                                <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.placed}/>
                                            </Display>
                                            <div className={"font-weight-400 hierarchy-leaf"}>
                                                {AppTranslations.userCreationProcess.allCompany}
                                            </div>
                                        </>
                                    }
                                    value={"all_companies"}
                                />
                                : undefined
                            }
                            {
                                completeHierarchiesTree?.filter(h => h.hierarchyGuid !== commercialHierarchyGUID)
                                    ?.map(hierarchyToFilteredVeboxTreeItem(
                                        searchWord.toLowerCase(),
                                        last((hierarchiesSelected?.selectedHierarchies as string[])),
                                        hasFullVisibility,
                                        false,
                                        isInCeasedCompany ? currentUser.company.guid : ""))
                                    ?.filter(c => c !== null && c !== undefined)
                            }
                            {
                                commercialHierarchy
                                && ((commercialHierarchy.children ?? [])?.filter(
                                    hierarchyIncludesWord(searchWord)).length > 0 || hasFullVisibility)
                                &&
                                (
                                    <VeboxTreeView.Radio
                                        key={commercialHierarchy?.hierarchyGuid}
                                        label={
                                            <>
                                                <Display isVisible={hasFullVisibility}>
                                                    <VisibilityIcon iconName={VISIBILITY_TREE_ICONS.visibility}/>
                                                </Display>
                                                <div className={"font-weight-400 hierarchy-leaf"}>
                                                    {commercialHierarchy?.label}
                                                </div>
                                            </>
                                        }
                                        expanded={commercialHierarchyExpanded}
                                    >
                                        <VeboxTreeView.CheckboxGroup
                                            values={hierarchiesSelected?.selectedCommercialHierarchies ?? []}
                                            onChange={changeCommercialHierarchies}
                                        >
                                            {(commercialHierarchy?.children ?? []).map(
                                                hierarchyToFilteredVeboxCheckboxTree(
                                                    searchWord,
                                                    selectedCommercialNodes, false, hasFullVisibility))}
                                        </VeboxTreeView.CheckboxGroup>
                                    </VeboxTreeView.Radio>
                                )
                            }
                        </VeboxTreeView.RadioGroup>
                    </div>
                    <Display isVisible={isVisibilityChanged}>
                        <div className={"user-details-buttons-section"}>
                            <Button
                                onClick={resetValues}
                                variant="tertiary"
                                style={{minWidth: 70}}
                                styles={BUTTON_STYLE}
                            >
                                {AppTranslations.genericWords.cancel}
                            </Button>
                            <Tooltip
                                message={AppTranslations.usersManagement.errorUserWithoutVisibility}
                                disableToolTip={isSubmittable}
                                placement={"top"}
                            >
                                <Button
                                    onClick={submitNewVisibility}
                                    variant="strong"
                                    style={{minWidth: 70}}
                                    styles={BUTTON_STYLE}
                                    disabled={!isSubmittable}
                                >
                                    {AppTranslations.genericWords.submit}
                                </Button>
                            </Tooltip>
                        </div>
                    </Display>
                </Display>
            </VeBoxCard.Body>
        </VeBoxCard>
    </>

}
